import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { useShortcut } from 'utils/hooks/keyboard/useShortcut';
import ListItem from 'components/ListItem';
import { useBreakpoints } from 'utils/hooks/UI';
import UIButton from 'components/UIElements/UIButton';
import {
  getBalance,
  getPaymentsTotal,
  getTip,
  getIsPartialPaymentAllowed,
  getIsEligibleForCashRounding,
  getChange,
  getCardPaymentsSum,
  getCardPaymentKeys,
} from 'reducers/Payments';
import {
  processPayments,
  setPaymentSelected,
  updateCurrency,
  askForCheckNumbers,
} from 'actions/Payments';
import {
  getDefaultCurrency,
  getUseAgeVerification,
} from 'reducers/configs/settings';
import { openModalPage } from 'actions/modalPage';
import { modalPages as mp } from 'constants/modalPage';
import { PluginComponent } from 'plugins';
import { getCurrencyByCode } from 'reducers/configs/currency';
import { useAsyncFunctionNoParallelExecution } from 'utils/hooks/useSingleUseFunction';
import { addWarning } from 'actions/Error';
import { KeyboardShortcut } from 'components/KeyboardShortcut';

const ConfirmPayment = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation('payment');
  const isMobileView = !useBreakpoints().mdplus;
  const tip = useSelector(getTip);
  const total = useSelector(getPaymentsTotal);
  const balance = useSelector(getBalance);
  const change = useSelector(getChange);
  const isReturnPayment = tip + total < 0;
  const useAgeVerification = useSelector(getUseAgeVerification);
  const defaultCurrencyCode = useSelector(getDefaultCurrency);
  const defaultCurrency = useSelector(getCurrencyByCode(defaultCurrencyCode));
  const totalCardPayments = useSelector(getCardPaymentsSum);
  const cardPaymentKeys = useSelector(getCardPaymentKeys);
  const isEligibleForCashRounding = useSelector(getIsEligibleForCashRounding);

  const isPartialPaymentAllowed = useSelector(getIsPartialPaymentAllowed);

  const shouldConfirmBeDisabled = useMemo(() => {
    if (isPartialPaymentAllowed) {
      return false;
    }

    // If eligible for rounding, then change not being negative means the customer is close enough to the total
    // otherwise, they must reach the total perfectly so we check the unrounded balance instead
    // i.e.
    //   Total | Payments | Allow
    //   5.25    4.00       Block regardless because balance is -1.25 and change is -1.00
    //   5.25    5.00       Allow if eligible for rounding, because change is -0.00
    //   5.25    6.00       Allow always because customer has overpaid
    const compare = isEligibleForCashRounding ? change : balance;
    return isReturnPayment
      ? 0 < compare // For returns, underpayment is when change/balance is positive
      : compare < 0; // For sales, underpayment is when change/balance is negative
  }, [
    isPartialPaymentAllowed,
    isEligibleForCashRounding,
    isReturnPayment,
    balance,
    change,
  ]);

  const confirmDisabledDueToCardOverpayment = useMemo(() => {
    const totalToPay = tip + total;
    const hasCardPayments = cardPaymentKeys.length > 0;
    // If there's no change to give out, assume no overpayments
    if (change === 0 || !hasCardPayments) {
      return false;
    }
    // Card payments less than total
    if (!isReturnPayment && totalCardPayments <= totalToPay) {
      return false;
    }
    if (isReturnPayment && totalCardPayments >= totalToPay) {
      return false;
    }
    return true;
  }, [
    cardPaymentKeys.length,
    change,
    isReturnPayment,
    tip,
    total,
    totalCardPayments,
  ]);

  const [
    confirmHandler,
    { isUsed: isConfirming },
  ] = useAsyncFunctionNoParallelExecution(async () => {
    // if user has entered foreign currency and skipped pressing OK,
    // update the currency back to default before payment is processed
    await dispatch(updateCurrency(defaultCurrency));

    if (useAgeVerification) {
      await dispatch(
        openModalPage({
          component: mp.ageVerification,
          isPopup: true,
          props: {
            setPaymentSelected,
            processPayments,
          },
        }),
      );
      return;
    }
    // If user pays with card in a way that it requires change - block it
    if (confirmDisabledDueToCardOverpayment) {
      dispatch(
        addWarning(t('alerts.cardOverPaymentNotAllowed'), {
          selfDismiss: 4000,
        }),
      );
      return;
    }

    if (!shouldConfirmBeDisabled) {
      await dispatch(askForCheckNumbers);
      await dispatch(setPaymentSelected(''));
      await dispatch(processPayments());
      setTimeout(() => localStorage.removeItem('selectedReason'), 10000);
    }
  });

  useShortcut('confirmPayments', confirmHandler, 20);

  return isMobileView ? (
    <PluginComponent
      hookname="UIMobilePaymentsModalPayButton"
      props={{
        disabled: shouldConfirmBeDisabled,
        confirmHandler,
      }}
    >
      <UIButton
        disabled={shouldConfirmBeDisabled || isConfirming}
        text={t('buttons.confirmPayment')}
        block={true}
        action={confirmHandler}
        className="confirm-payment"
      />
    </PluginComponent>
  ) : (
    <PluginComponent
      hookname="UIPaymentsModalPayButton"
      props={{
        disabled: shouldConfirmBeDisabled,
        confirmHandler,
      }}
    >
      <ListItem
        disabled={shouldConfirmBeDisabled || isConfirming}
        variant="grid_green"
        // @ts-ignore
        action={confirmHandler}
        data-testid="confirm-payment-button"
      >
        {/* @ts-ignore */}
        <>
          <span className="icon_check" />
          <KeyboardShortcut
            shortcutAction="confirmPayments"
            className="func-key"
            data-testid="confirm-payment-shortcut"
          />
        </>
      </ListItem>
    </PluginComponent>
  );
};

export default ConfirmPayment;
